// frontend/app/components/lessonPlans/LessonPlanWorkspace.js
//
// The Lesson Plan runtime shell: header, persistent phase rail, step body and
// step navigation. Renders whatever the API hands it — there is no per-lesson
// branch anywhere in this tree, and no lesson content in the bundle.

import React, { useCallback, useMemo, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import LessonBlockRenderer from "./LessonBlockRenderer";
import LessonPwoPlayer from "./LessonPwoPlayer";
import LessonQuizPlayer from "./LessonQuizPlayer";
import LessonVideoPlayer from "./LessonVideoPlayer";
import LessonCoverPage from "./LessonCoverPage";
import LessonDownloadButton from "./LessonDownloadButton";
import LessonPhaseRail from "./LessonPhaseRail";
import { ActionButton, Pill } from "./blocks/LessonBlockShell";
import { getLessonColors, getSectionMeta } from "./lessonPlanTheme";
import {
  ASSET_TIER_EXTENDED,
  assetTierOf,
  groupBlocksByAssetTier,
  groupStepsBySection,
  isAssetBlock,
  quizAnswersFor,
  stepDisplayTitle,
} from "./lessonPlanRules";
import useLessonBlockHandlers from "./useLessonBlockHandlers";

export const COVER_INDEX = -1;

// Below this the rail is dropped and the footer Back/Next carries navigation.
const RAIL_MIN_WIDTH = 900;

const StepHeader = ({
  step,
  colors,
  sectionIndex,
  sectionTotal,
  sectionMeta,
  // The step's display title (an authored title, else the section label or
  // "Step N" — phases are a hidden key and never shown).
  displayTitle,
  mode,
  t,
}) => {
  const translate = t || ((value) => value);

  return (
    <View style={[styles.stepHeader, { borderBottomColor: colors.border }]}>
      <View style={styles.stepHeaderTop}>
        <View style={[styles.phaseBadge, { backgroundColor: `${sectionMeta.accent}22` }]}>
          <MaterialCommunityIcons name={sectionMeta.icon} size={18} color={sectionMeta.accent} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[styles.stepEyebrow, { color: sectionMeta.accent }]}>
            {translate("Section")} {sectionIndex + 1} {translate("of")} {sectionTotal} · {translate(sectionMeta.label)}
          </Text>
          <Text style={[styles.stepTitle, { color: colors.textPrimary }]}>
            {displayTitle}
          </Text>
        </View>
      </View>

      {mode === "teacher" && step.student_activity ? (
        <View style={styles.stepMeta}>
          <Pill colors={colors} label={translate("Students work along")} icon="account-group-outline" />
        </View>
      ) : null}
    </View>
  );
};

const LessonPlanWorkspace = ({
  lesson,
  isDarkMode = false,
  mode = "teacher",
  activeIndex = COVER_INDEX,
  onActiveIndexChange,
  sessionState = {},
  onSessionStateChange,
  readOnly = false,
  launchingBlockId = null,
  onLaunchBlock,
  onAsk,
  // Shows an authored asset summary in the Saras rail beside the lesson,
  // without an ask. Optional: unwired, an authored summary opens inside its own
  // asset card instead, so the control still does something.
  onShowSummary,
  // The 3D scene plays inline, directly under the block that opened it, using
  // the lesson's own player rather than the Learning module's global iframe.
  openAssetBlockId = null,
  onCloseAsset,
  authToken,
  localLanguage = "en",
  languageId = 1,
  onClose,
  onFinish,
  onOpenInsights,
  // Authenticated fetch for the teacher's "Download JSON" action on the
  // overview. Absent, the action is simply not drawn.
  fetchWithAuth,
  // Present mode (2026-08-27). Teacher-only: a student is not presenting to
  // anyone. Absent handler means the caller has not wired it, so no button.
  onPresent,
  // Saras Learning Loop. The workspace neither fetches nor evaluates: it
  // reports what the student did and renders what it is handed. `mastery`
  // feeds the Grow phase's mastery_map block; the two callbacks reach the
  // loop endpoints, and both are optional so a caller that has not wired
  // them yet still gets a working lesson.
  mastery = null,
  onAiEvaluate,
  onExplorationEvent,
  t,
}) => {
  const translate = t || ((value) => value);
  const colors = getLessonColors(isDarkMode);
  const { width: windowWidth } = useWindowDimensions();

  // The workspace shares the content area with the Saras rail, so window width
  // overstates how much room it actually has. Measure the container instead and
  // fall back to the window only for the very first frame.
  const [measuredWidth, setMeasuredWidth] = useState(0);
  const width = measuredWidth || windowWidth;
  const railVisible = width >= RAIL_MIN_WIDTH;

  const steps = useMemo(
    () => (Array.isArray(lesson?.steps) ? lesson.steps : []),
    [lesson]
  );
  const conceptLabels = useMemo(() => {
    const map = {};
    (lesson?.concepts || []).forEach((concept) => {
      map[concept.concept_key] = concept.label;
    });
    return map;
  }, [lesson]);

  // The same grouping the rail uses, so the two never disagree about which
  // section a step belongs to or how many sections the lesson has.
  const sections = useMemo(() => groupStepsBySection(steps), [steps]);

  const {
    handleAnswer,
    handleCheck,
    handleLaunch,
    handleHintRequest,
    handleQuizAnswer,
  } = useLessonBlockHandlers({
    sessionState,
    onSessionStateChange,
    readOnly,
    onLaunchBlock,
    onAiEvaluate,
    onExplorationEvent,
  });

  /**
   * Answers already recorded inside one quiz block, in the shape the inline
   * player takes them: `{ [questionId]: value }`.
   *
   * Session state is flat and keyed by composite block id, which is a detail
   * the player has no business knowing — it is handed a plain answer map and
   * reports plain answers back.
   */
  const answeredQuizValues = useCallback(
    (blockId) => {
      const values = {};
      quizAnswersFor(sessionState, blockId).forEach(({ questionId, entry }) => {
        values[questionId] = entry.value;
      });
      return values;
    },
    [sessionState]
  );

  const goTo = useCallback(
    (nextIndex) => {
      const clamped = Math.max(COVER_INDEX, Math.min(nextIndex, steps.length - 1));
      if (onActiveIndexChange) onActiveIndexChange(clamped);
    },
    [onActiveIndexChange, steps.length]
  );

  const activeStep =
    activeIndex >= 0 && activeIndex < steps.length ? steps[activeIndex] : null;

  // Where the active step sits in the section grouping, for the step header's
  // "Section N of M" eyebrow. Falls back to a single, whole-lesson section if
  // the active step somehow is not found in any group — it always will be,
  // since groupStepsBySection never drops a step.
  const activeSectionInfo = useMemo(() => {
    if (!activeStep) return null;
    const sectionIndex = sections.findIndex((section) =>
      section.steps.some((entry) => entry.index === activeIndex)
    );
    if (sectionIndex === -1) {
      return {
        sectionIndex: 0,
        sectionTotal: sections.length || 1,
        meta: getSectionMeta(null),
        indexInSection: 0,
        sectionStepCount: 1,
      };
    }
    const section = sections[sectionIndex];
    return {
      sectionIndex,
      sectionTotal: sections.length,
      meta: section.meta,
      indexInSection: section.steps.findIndex((entry) => entry.index === activeIndex),
      sectionStepCount: section.steps.length,
    };
  }, [sections, activeStep, activeIndex]);

  const unresolvedCount = lesson?.validation?.unresolved_count || 0;

  /**
   * One block of the active step, plus the inline player it opens into when
   * it is the currently-launched asset. Pulled out of the render tree below
   * because an asset block is now reached two ways — directly from the step's
   * own block list, and from inside an asset-tier group — and both must stay
   * byte-for-byte the same block.
   */
  const renderStepBlock = (block) => {
    const isOpenAsset =
      block.id &&
      block.id === openAssetBlockId &&
      block.resolution_status === "resolved";
    const showPwoHere = isOpenAsset && block.type === "saras_pwo";
    const showVideoHere = isOpenAsset && block.type === "saras_video";
    const showQuizHere = isOpenAsset && block.type === "saras_topic_quiz";
    // The group heading above says "Core assets"; only Extended cards keep a
    // pill, so the optional ones still read as optional on their own.
    const isExtended = isAssetBlock(block) && assetTierOf(block) === ASSET_TIER_EXTENDED;

    return (
      <React.Fragment key={block.id || `${activeStep.step_key}-${block.type}`}>
        {isExtended ? (
          <View style={styles.assetTierRow}>
            <Pill
              colors={colors}
              label={translate("Optional")}
              accent={colors.textMuted}
            />
          </View>
        ) : null}

        <LessonBlockRenderer
          block={block}
          colors={colors}
          mastery={mastery}
          onHintRequest={handleHintRequest}
          language={localLanguage}
          onExplorationEvent={onExplorationEvent}
          isDarkMode={isDarkMode}
          mode={mode}
          sessionState={sessionState}
          conceptLabels={conceptLabels}
          readOnly={readOnly}
          launchingBlockId={launchingBlockId}
          onAnswer={handleAnswer}
          onCheck={handleCheck}
          onLaunch={handleLaunch}
          onAsk={onAsk}
          onShowSummary={onShowSummary}
          t={t}
        />

        {showPwoHere ? (
          <LessonPwoPlayer
            // Keyed on the block only: switching scenes within the
            // same PWO must reuse this instance so Unity hot-swaps
            // instead of cold-booting.
            key={`pwo-${block.id}`}
            sourcePwoId={block.resolved_asset?.source_pwo_id}
            sceneName={block.resolved_asset?.scene_name}
            authToken={authToken}
            localLanguage={localLanguage}
            isDarkMode={isDarkMode}
            onClose={onCloseAsset}
            t={t}
          />
        ) : null}

        {showVideoHere ? (
          <LessonVideoPlayer
            key={`video-${block.id}`}
            sourceVideoId={block.resolved_asset?.source_video_id}
            tagId={block.resolved_asset?.tag_id}
            authToken={authToken}
            languageId={languageId}
            isDarkMode={isDarkMode}
            onClose={onCloseAsset}
            t={t}
          />
        ) : null}

        {showQuizHere ? (
          <LessonQuizPlayer
            key={`quiz-${block.id}`}
            lessonPlanId={lesson?.lesson_plan_id}
            blockId={block.id}
            stepKey={activeStep.step_key}
            // Answers already recorded for this block, so
            // stepping away and back shows the student their own
            // work rather than an empty quiz.
            initialAnswers={answeredQuizValues(block.id)}
            authToken={authToken}
            languageId={languageId}
            isDarkMode={isDarkMode}
            readOnly={readOnly}
            onAnswer={(answer) => handleQuizAnswer(block, answer)}
            onClose={onCloseAsset}
            t={t}
          />
        ) : null}
      </React.Fragment>
    );
  };

  return (
    <View
      style={[styles.container, { backgroundColor: colors.pageBg }]}
      onLayout={(event) => setMeasuredWidth(event.nativeEvent.layout.width)}
    >
      {/* ── Header ─────────────────────────────────────────────── */}
      <View
        style={[
          styles.header,
          { backgroundColor: colors.panelBg, borderColor: colors.border },
        ]}
      >
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={translate("Close lesson")}
          style={[styles.closeButton, { borderColor: colors.border }]}
        >
          <MaterialCommunityIcons name="arrow-left" size={18} color={colors.textSecondary} />
        </TouchableOpacity>

        <View style={styles.headerText}>
          <Text style={[styles.lessonTitle, { color: colors.textPrimary }]} numberOfLines={1}>
            {translate("Lesson")} {lesson?.lesson_no} · {lesson?.title}
          </Text>
        </View>

        {/* The duration, difficulty and step-count pills and the subject ›
            chapter › topic breadcrumb used to sit here. All four restated
            something the teacher had already read on the overview or can read
            below in the phase rail, and between them they buried the one
            control this screen exists to offer. */}
        <View style={styles.headerMeta}>
          {mode === "teacher" && onPresent ? (
            // The only filled control on the screen, following ActionButton's
            // solid variant so the projector button reads the same as the
            // footer's primary. Nothing else in the header may carry this
            // weight — that is the whole point of the button.
            <TouchableOpacity
              onPress={onPresent}
              accessibilityRole="button"
              accessibilityLabel={translate("Present lesson")}
              style={[styles.presentButton, { backgroundColor: colors.accentPrimary }]}
            >
              <MaterialCommunityIcons name="presentation" size={16} color="#ffffff" />
              <Text style={styles.presentText}>{translate("Present")}</Text>
            </TouchableOpacity>
          ) : null}
          {mode === "teacher" && fetchWithAuth && activeIndex === COVER_INDEX ? (
            <LessonDownloadButton
              fetchWithAuth={fetchWithAuth}
              lessonPlanId={lesson?.lesson_plan_id}
              color={colors.textSecondary}
              borderColor={colors.border}
              t={t}
            />
          ) : null}
          {mode === "teacher" && onOpenInsights ? (
            // Icon only, and deliberately not accentPrimary: a second tinted
            // control beside Present competes with it. The label lives on
            // accessibilityLabel, which is also how it stays testable by name.
            <TouchableOpacity
              onPress={onOpenInsights}
              accessibilityRole="button"
              accessibilityLabel={translate("Teacher insights")}
              style={[styles.insightsButton, { borderColor: colors.border }]}
            >
              <MaterialCommunityIcons
                name="chart-box-outline"
                size={16}
                color={colors.textSecondary}
              />
            </TouchableOpacity>
          ) : null}
        </View>
      </View>

      {unresolvedCount > 0 && mode === "teacher" ? (
        <View
          style={[
            styles.warning,
            { backgroundColor: colors.teacherTipBg, borderColor: colors.teacherTipBorder },
          ]}
        >
          <MaterialCommunityIcons name="alert-outline" size={16} color={colors.warning} />
          <Text style={[styles.warningText, { color: colors.textPrimary }]}>
            {unresolvedCount} {translate("resource(s) in this lesson could not be matched to the current curriculum.")}
          </Text>
        </View>
      ) : null}

      {/* ── Body ───────────────────────────────────────────────── */}
      <View style={styles.body}>
        {railVisible ? (
          <View style={styles.railColumn}>
            <LessonPhaseRail
              steps={steps}
              activeIndex={activeIndex}
              sessionState={sessionState}
              colors={colors}
              onSelect={goTo}
              onSelectCover={() => goTo(COVER_INDEX)}
              t={t}
            />
          </View>
        ) : null}

        <View
          style={[
            styles.stageColumn,
            { backgroundColor: colors.panelBg, borderColor: colors.border },
          ]}
        >
          <ScrollView
            style={styles.stageScroll}
            contentContainerStyle={styles.stageContent}
            showsVerticalScrollIndicator={false}
          >
            {activeStep ? (
              <>
                <StepHeader
                  step={activeStep}
                  colors={colors}
                  sectionIndex={activeSectionInfo.sectionIndex}
                  sectionTotal={activeSectionInfo.sectionTotal}
                  sectionMeta={activeSectionInfo.meta}
                  displayTitle={stepDisplayTitle(
                    activeStep,
                    activeSectionInfo.indexInSection,
                    activeSectionInfo.sectionStepCount,
                    translate
                  )}
                  mode={mode}
                  t={t}
                />

                {mode === "teacher" && activeStep.teacher_guidance ? (
                  <View
                    style={[
                      styles.guidance,
                      {
                        backgroundColor: colors.aiCardBg,
                        borderColor: colors.aiCardBorder,
                      },
                    ]}
                  >
                    <Text style={[styles.guidanceLabel, { color: colors.accentPrimary }]}>
                      {translate("What you do")}
                    </Text>
                    <Text style={[styles.guidanceText, { color: colors.textPrimary }]}>
                      {activeStep.teacher_guidance}
                    </Text>
                    {activeStep.student_activity ? (
                      <>
                        <Text
                          style={[
                            styles.guidanceLabel,
                            { color: colors.accentPrimary, marginTop: 10 },
                          ]}
                        >
                          {translate("What students do")}
                        </Text>
                        <Text style={[styles.guidanceText, { color: colors.textPrimary }]}>
                          {activeStep.student_activity}
                        </Text>
                      </>
                    ) : null}
                  </View>
                ) : null}

                {groupBlocksByAssetTier(activeStep.blocks || []).map((entry, entryIndex) => {
                  if (entry.kind === "block") {
                    return renderStepBlock(entry.block);
                  }

                  // An asset group: always headed (see groupBlocksByAssetTier),
                  // then each of its blocks rendered exactly as any other.
                  return (
                    <React.Fragment
                      key={`asset-group-${activeStep.step_key}-${entry.tier}-${entryIndex}`}
                    >
                      {entry.heading ? (
                        <Text style={[styles.assetGroupHeading, { color: colors.textMuted }]}>
                          {translate(
                            entry.tier === ASSET_TIER_EXTENDED
                              ? "Extended assets (optional)"
                              : "Core assets"
                          )}
                        </Text>
                      ) : null}
                      {entry.blocks.map((block) => renderStepBlock(block))}
                    </React.Fragment>
                  );
                })}
              </>
            ) : (
              <LessonCoverPage
                lesson={lesson}
                colors={colors}
                mode={mode}
                wide={width >= 900}
                onAsk={onAsk}
                t={t}
              />
            )}
          </ScrollView>

          {/* ── Step navigation ─────────────────────────────────── */}
          <View style={[styles.footer, { borderTopColor: colors.border }]}>
            <ActionButton
              colors={colors}
              label={activeIndex <= 0 ? translate("Overview") : translate("Back")}
              icon="arrow-left"
              variant="outline"
              onPress={() => goTo(activeIndex - 1)}
              disabled={activeIndex === COVER_INDEX}
            />
            <View style={{ flex: 1 }} />
            {activeIndex < steps.length - 1 ? (
              <ActionButton
                colors={colors}
                label={
                  activeIndex === COVER_INDEX
                    ? translate("Start lesson")
                    : translate("Next")
                }
                icon="arrow-right"
                onPress={() => goTo(activeIndex + 1)}
              />
            ) : (
              <ActionButton
                colors={colors}
                label={translate("Finish lesson")}
                icon="flag-checkered"
                accent={colors.success}
                // Records completion so the lessons page shows the badge, then
                // closes. Falls back to a plain close if no handler is wired.
                onPress={onFinish || onClose}
              />
            )}
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, width: "100%", minHeight: 0, gap: 12 },

  header: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 16,
    borderWidth: 1,
    paddingHorizontal: 14,
    paddingVertical: 12,
    gap: 12,
  },
  closeButton: {
    width: 34,
    height: 34,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: { flex: 1, minWidth: 0 },
  lessonTitle: { fontSize: 18, fontWeight: "800" },
  headerMeta: { flexDirection: "row", alignItems: "center", gap: 8, flexWrap: "wrap" },
  // Roomier than the outline it replaced. A primary that is merely coloured
  // still reads as one more chip in a row of chips; the size is half of what
  // makes it findable at a glance from the back of a classroom.
  presentButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 8,
    gap: 6,
  },
  presentText: { fontSize: 12, fontWeight: "800", color: "#ffffff", marginLeft: 6 },
  insightsButton: {
    width: 32,
    height: 32,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  warning: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  warningText: { fontSize: 13, flex: 1, marginLeft: 8 },

  body: { flex: 1, minHeight: 0, flexDirection: "row", gap: 12 },
  railColumn: { width: 260, minWidth: 260, flexShrink: 0, minHeight: 0 },
  stageColumn: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    borderRadius: 16,
    borderWidth: 1,
    overflow: "hidden",
  },
  stageScroll: { flex: 1 },
  // 1140 = the 1100 the inline player needs plus the padding. Inert below that;
  // it exists so prose on a 2560 display with the rail closed does not run the
  // full width of the stage.
  stageContent: { padding: 20, width: "100%", maxWidth: 1140, alignSelf: "center" },

  stepHeader: { paddingBottom: 14, marginBottom: 16, borderBottomWidth: 1 },
  stepHeaderTop: { flexDirection: "row", alignItems: "flex-start", gap: 12 },
  phaseBadge: {
    width: 38,
    height: 38,
    borderRadius: 11,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  stepEyebrow: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
  },
  stepTitle: { fontSize: 20, fontWeight: "800", marginTop: 3 },
  stepMeta: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 12 },

  assetGroupHeading: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginTop: 16,
    marginBottom: 8,
  },
  assetTierRow: { flexDirection: "row", marginBottom: 6 },

  guidance: {
    borderRadius: 14,
    borderWidth: 1,
    padding: 14,
    marginBottom: 14,
  },
  guidanceLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  guidanceText: { fontSize: 14, lineHeight: 22 },

  footer: {
    flexDirection: "row",
    alignItems: "center",
    borderTopWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 10,
  },
});

export default LessonPlanWorkspace;
